/**
 * /finance/close — Period Close Checklist
 * Derives month-end completion state from canonical signals across AR,
 * pay runs, field-adds, disputes, holdbacks, and AP. Read-only — closing
 * itself is a future write-path tied to a CloseRecord ledger.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, AlertTriangle, Clock, ShieldAlert, ChevronRight, Lock, Unlock } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitShell";
import { useAppData } from "@/data/AppDataContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { usePayRunViews } from "@/data/finance/payRun/usePayRunViews";
import { getPendingFieldAdds } from "@/data/finance/payRun/fieldAdds";
import { deriveAllHoldbackStates } from "@/data/contractor/networkExecution/holdback/deriveState";
import { deriveAllDisputes } from "@/data/contractor/networkExecution/dispute/dispute";
import {
  buildPeriodClose,
  buildBillsFromPOs,
  type CloseCheckItem,
} from "@/data/finance/financeWorkbenchSelectors";
import { FinanceNavStrip } from "@/components/finance/FinanceTabs";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";


function StatusIcon({ status }: { status: CloseCheckItem["status"] }) {
  if (status === "complete") return <CheckCircle2 className="h-5 w-5 text-success" />;
  if (status === "blocked") return <AlertTriangle className="h-5 w-5 text-destructive" />;
  if (status === "in-progress") return <Clock className="h-5 w-5 text-warning" />;
  return <Circle className="h-5 w-5 text-muted-foreground/50" />;
}

const STATUS_LABEL: Record<CloseCheckItem["status"], string> = {
  complete: "Complete",
  "in-progress": "In progress",
  blocked: "Blocked",
  pending: "Pending",
};

const STATUS_TONE: Record<CloseCheckItem["status"], string> = {
  complete: "text-success",
  "in-progress": "text-warning",
  blocked: "text-destructive",
  pending: "text-muted-foreground",
};

export default function PeriodClosePage() {
  const appData = useAppData();
  const { jobs, holdbackLedger, disputeEvents } = appData;
  const fieldAddSubmissions: any[] = (appData as any).fieldAddSubmissions ?? [];
  const payRunRecords: any[] = (appData as any).payRunRecords ?? [];
  const { heldRows } = usePayRunViews();
  const { purchaseOrders } = useVendorPO();
  const fin$ = useFinancialPermissions();
  const { role } = usePermissions();
  const { isPeriodClosed, closePeriod, reopenPeriod } = useGeneralLedger();
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");

  const close = useMemo(() => {
    const pendingFieldAdds = getPendingFieldAdds(fieldAddSubmissions);
    const holdbackStates = deriveAllHoldbackStates(holdbackLedger);
    const disputeStates = deriveAllDisputes(disputeEvents);
    const bills = buildBillsFromPOs(purchaseOrders);
    return buildPeriodClose({
      jobs, payRunRecords, heldRows, pendingFieldAdds, holdbackStates, disputeStates, bills,
    });
  }, [jobs, payRunRecords, heldRows, fieldAddSubmissions, holdbackLedger, disputeEvents, purchaseOrders]);

  const periodKey = close.monthStartIso.slice(0, 7);
  const periodIsClosed = isPeriodClosed(periodKey);
  const canClose = role === "super_admin" || role === "owner" || role === "admin";

  const handleClose = () => {
    try {
      closePeriod(periodKey, role ?? "user", reason || undefined);
      toast({ title: `Period ${close.periodLabel} closed`, description: "Revenue and expense rolled to Retained Earnings." });
      setConfirmOpen(false);
      setConfirmText("");
      setReason("");
    } catch (e) {
      toast({ variant: "destructive", title: "Close failed", description: e instanceof Error ? e.message : String(e) });
    }
  };

  const handleReopen = () => {
    try {
      reopenPeriod(periodKey, reason || "Reopen requested", role ?? "user");
      toast({ title: `Period ${close.periodLabel} reopened`, description: "Retained Earnings roll reversed." });
      setReopenOpen(false);
      setReason("");
    } catch (e) {
      toast({ variant: "destructive", title: "Reopen failed", description: e instanceof Error ? e.message : String(e) });
    }
  };

  if (!fin$.viewFinancialSummary) {
    return (
      <CockpitPage>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <ShieldAlert className="h-6 w-6 text-destructive mb-3" />
          <h1 className="text-xl font-semibold tracking-tight mb-2">Access Restricted</h1>
        </div>
      </CockpitPage>
    );
  }


  const blockedCount = close.checks.filter((c) => c.status === "blocked").length;
  const readyToClose = close.checks.every((c) => c.status === "complete");

  return (
    <CockpitPage>
      <header className="pb-3 border-b border-border/60">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">Period Close · {close.periodLabel}</h1>
        <p className="text-[12px] text-muted-foreground mt-1">
          {close.checks.filter((c) => c.status === "complete").length} of {close.checks.length} checks complete
          {blockedCount > 0 ? ` · ${blockedCount} blocked` : ""}
          {` · ${close.daysIntoMonth} day${close.daysIntoMonth === 1 ? "" : "s"} after period end`}
        </p>
      </header>

      <FinanceNavStrip />

      {/* Progress bar */}
      <div className="bg-background border border-border/60 p-4">
        <div className="flex items-baseline justify-between mb-2">
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Close progress</p>
          <p className="text-[20px] font-semibold tabular-nums">{close.completionPct.toFixed(0)}%</p>
        </div>
        <div className="h-1.5 bg-muted overflow-hidden">
          <div
            className={`h-full ${close.completionPct === 100 ? "bg-success" : close.completionPct >= 80 ? "bg-primary" : "bg-warning"}`}
            style={{ width: `${close.completionPct}%` }}
          />
        </div>
        {readyToClose ? (
          <div className="mt-3 px-3 py-2 bg-success/10 text-success text-[12px] flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            All checks complete. Period is ready to close.
          </div>
        ) : blockedCount > 0 ? (
          <div className="mt-3 px-3 py-2 bg-destructive/10 text-destructive text-[12px] flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {blockedCount} check{blockedCount === 1 ? "" : "s"} blocked — resolve before closing.
          </div>
        ) : (
          <div className="mt-3 px-3 py-2 bg-muted/40 text-muted-foreground text-[12px]">
            Work through the checklist below. Period closes after every check is complete.
          </div>
        )}
      </div>

      {/* Checklist */}
      <div className="bg-background border border-border/60">
        {close.checks.map((check) => {
          const content = (
            <div className="grid grid-cols-[28px_1fr_140px_24px] gap-3 px-4 py-3.5 border-b border-border/40 last:border-0 hover:bg-muted/20 items-center">
              <StatusIcon status={check.status} />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-foreground">{check.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{check.description}</p>
              </div>
              <div className="text-right">
                <p className={`text-[11px] uppercase tracking-[0.12em] font-medium ${STATUS_TONE[check.status]}`}>{STATUS_LABEL[check.status]}</p>
                <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5">{check.detail}</p>
              </div>
              {check.href ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <span />}
            </div>
          );
          return check.href ? (
            <Link key={check.id} to={check.href} className="block">{content}</Link>
          ) : (
            <div key={check.id}>{content}</div>
          );
        })}
      </div>

      {/* Close / Reopen action */}
      <div className="bg-background border border-border/60 p-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-foreground flex items-center gap-2">
            {periodIsClosed ? <Lock className="h-4 w-4 text-success" /> : <Unlock className="h-4 w-4 text-muted-foreground" />}
            {periodIsClosed ? `${close.periodLabel} is closed` : `${close.periodLabel} is open`}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {periodIsClosed
              ? "Revenue and expense rolled to Retained Earnings (3900). New postings to this period are blocked."
              : canClose
                ? "Closing posts the retained-earnings roll and freezes all postings for the period."
                : "Only owner, admin, or super admin can close a period."}
          </p>
        </div>
        {periodIsClosed ? (
          <Button variant="outline" size="sm" disabled={!canClose} onClick={() => setReopenOpen(true)}>
            Reopen period
          </Button>
        ) : (
          <Button size="sm" disabled={!canClose || !readyToClose} onClick={() => setConfirmOpen(true)}>
            Close period
          </Button>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close {close.periodLabel}?</AlertDialogTitle>
            <AlertDialogDescription>
              This posts a retained-earnings roll to account 3900 and freezes all new postings dated in {close.periodLabel}.
              Reopening reverses the roll but is auditable. Type <strong>CONFIRM</strong> to proceed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <Input
              autoFocus
              placeholder="Type CONFIRM"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
            <Textarea
              placeholder="Optional reason / close notes"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setConfirmText(""); setReason(""); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={confirmText !== "CONFIRM"} onClick={handleClose}>
              Close period
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen {close.periodLabel}?</AlertDialogTitle>
            <AlertDialogDescription>
              Reopening reverses the retained-earnings roll and allows new postings dated in this period.
              A reason of at least 10 characters is required for the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            autoFocus
            placeholder="Why is this period being reopened?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReason("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={reason.trim().length < 10} onClick={handleReopen}>
              Reopen period
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CockpitPage>
  );
}

